import React, { useState } from 'react';
import { X, Rocket, CheckCircle, Server, Shield, Activity, AlertTriangle } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

interface DeploymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectName: string;
}

export const DeploymentModal: React.FC<DeploymentModalProps> = ({ isOpen, onClose, projectName }) => {
  const { addToast } = useToast();
  const [environment, setEnvironment] = useState('dev');
  const [versionType, setVersionType] = useState('minor');
  const [isDeploying, setIsDeploying] = useState(false);
  const [step, setStep] = useState<'config' | 'deploying' | 'success'>('config');

  if (!isOpen) return null;

  const handleDeploy = () => {
    setIsDeploying(true);
    setStep('deploying');
    
    // Simulate deployment process
    setTimeout(() => {
        setIsDeploying(false);
        setStep('success');
        addToast({ type: 'success', title: 'Deployment Successful', message: `Deployed to ${environment.toUpperCase()}` });
    }, 2500);
  };

  const handleClose = () => {
      setStep('config');
      setIsDeploying(false);
      onClose();
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
       <div className="bg-white dark:bg-slate-950 w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200">
          
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
             <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-600 flex items-center justify-center">
                    <Rocket size={20} />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Deploy Workflow</h2>
                    <p className="text-xs text-slate-500">{projectName}</p>
                </div>
             </div>
             {!isDeploying && step !== 'success' && (
                 <button onClick={handleClose} className="text-slate-400 hover:text-slate-600">
                     <X size={20} />
                 </button>
             )}
          </div>

          {/* Content */}
          <div className="p-6">
              {step === 'config' && (
                  <div className="space-y-6">
                      {/* Environment Selection */}
                      <div className="space-y-3">
                          <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Target Environment</label>
                          <div className="grid grid-cols-3 gap-3">
                              {['dev', 'staging', 'prod'].map(env => (
                                  <button
                                    key={env}
                                    onClick={() => setEnvironment(env)}
                                    className={`
                                        flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all
                                        ${environment === env 
                                            ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300' 
                                            : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 text-slate-500'}
                                    `}
                                  >
                                      <span className="uppercase text-xs font-bold mb-1">{env}</span>
                                      {env === 'prod' ? <Shield size={16}/> : <Server size={16}/>}
                                  </button>
                              ))}
                          </div>
                      </div>

                      {/* Version Selection */}
                      <div className="space-y-3">
                          <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Release Strategy</label>
                          <select 
                            value={versionType}
                            onChange={(e) => setVersionType(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          >
                              <option value="patch">Patch Release (v1.0.x) - Bug fixes</option>
                              <option value="minor">Minor Release (v1.x.0) - New features</option>
                              <option value="major">Major Release (vx.0.0) - Breaking changes</option>
                          </select>
                      </div>

                      {environment === 'prod' && (
                          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900 rounded-lg flex gap-3 text-amber-800 dark:text-amber-400">
                              <AlertTriangle size={20} className="shrink-0" />
                              <div className="text-xs">
                                  <strong>Production Warning:</strong> This will immediately affect live traffic. Ensure all tests have passed in Staging.
                              </div>
                          </div>
                      )}
                  </div>
              )}

              {step === 'deploying' && (
                  <div className="py-8 flex flex-col items-center justify-center text-center space-y-4">
                      <div className="relative w-16 h-16">
                          <div className="absolute inset-0 rounded-full border-4 border-slate-200 dark:border-slate-800"></div>
                          <div className="absolute inset-0 rounded-full border-4 border-teal-500 border-t-transparent animate-spin"></div>
                      </div>
                      <div>
                          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">Deploying...</h3>
                          <p className="text-sm text-slate-500">Packaging and uploading to {environment.toUpperCase()}</p>
                      </div>
                      <div className="w-full max-w-xs bg-slate-100 dark:bg-slate-800 rounded-full h-2 mt-4 overflow-hidden">
                          <div className="h-full bg-teal-500 animate-progress-indeterminate"></div>
                      </div>
                  </div>
              )}

              {step === 'success' && (
                  <div className="py-6 flex flex-col items-center justify-center text-center space-y-4 animate-in zoom-in-50 duration-300">
                      <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-full flex items-center justify-center mb-2">
                          <CheckCircle size={40} />
                      </div>
                      <div>
                          <h3 className="text-xl font-bold text-slate-900 dark:text-white">Deployment Complete!</h3>
                          <p className="text-slate-500 mt-1">
                              Version <strong>v1.2.0</strong> is now live on <strong className="uppercase">{environment}</strong>.
                          </p>
                      </div>
                      <div className="flex items-center gap-2 mt-4 text-xs text-slate-400 bg-slate-50 dark:bg-slate-900 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-800">
                          <Activity size={12} />
                          <span>Health Check: 100% OK</span>
                      </div>
                  </div>
              )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex justify-end gap-3">
                {step === 'config' && (
                    <>
                        <button 
                            onClick={handleClose} 
                            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleDeploy} 
                            className="px-6 py-2 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-lg shadow-sm shadow-teal-600/20 transition-all flex items-center gap-2"
                        >
                            <Rocket size={16} /> Deploy Now
                        </button>
                    </>
                )}
                {step === 'success' && (
                    <button 
                        onClick={handleClose} 
                        className="px-6 py-2 text-sm font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-all"
                    >
                        Close
                    </button>
                )}
          </div>

       </div>
    </div>
  );
};